/**
 * Preloader utility for code-split components
 * Provides intelligent preloading strategies to improve UX
 */

// Component preloader functions
export const preloadComponents = {
  // Core app components
  Dashboard: () => import('../pages/Dashboard'),
  Profile: () => import('../pages/Profile'),
  
  // Property-related components (heaviest)
  FindPropertiesPage: () => import('../pages/FindPropertiesPage'),
  FindPropertiesMap: () => import('../pages/FindPropertiesMap'),
  PropertyDetail: () => import('../pages/PropertyDetail'),
  MyPropertyPage: () => import('../pages/MyPropertyPage'),
  MyPropertyDashboardPage: () => import('../pages/MyPropertyDashboardPage'),
  MyPropertiesPage: () => import('../pages/MyPropertiesPage'),
  MediaStudio: () => import('../pages/MediaStudio'),
  
  // Agent area
  AgentDashboard: () => import('../components/agent/AgentDashboard'),
  AgentSettingsPage: () => import('../pages/agent/AgentSettingsPage'),
  AgentOnboardingPage: () => import('../pages/agent/OnboardingPage'),
  // Opened from the dashboard's cards, which also warm them up on hover and focus.
  AgentEmailPage: () => import('../pages/agent/AgentEmailPage'),
  AgentCalendarPage: () => import('../pages/agent/AgentCalendarPage'),
  AgentContactsPage: () => import('../pages/agent/AgentContactsPage'),
  // The agent's listings and a listing's workspace, warmed up by the listing cards.
  AgentPropertiesPage: () => import('../pages/agent/AgentPropertiesPage'),
  AgentPropertyWorkspace: () => import('../pages/agent/AgentPropertyWorkspace'),
  // The approval queue, opened from the dashboard's approvals line and the workspace.
  AgentApprovalsPage: () => import('../pages/agent/AgentApprovalsPage'),
  // The agent's marketing (posts, print, e-mails), opened from the menu and a post's notice.
  AgentMarketingPage: () => import('../pages/agent/AgentMarketingPage'),
  // Tasks and inquiries, opened from the dashboard's Today strip.
  AgentTasksPage: () => import('../pages/agent/AgentTasksPage'),
  AgentInquiriesPage: () => import('../pages/agent/AgentInquiriesPage'),

  // Chat components
  ChatPage: () => import('../pages/ChatPage'),
  ChatInterface: () => import('../components/ChatInterface'),
  
  // Utility components
  GoogleMapComponent: () => import('../components/GoogleMapComponent'),
  PropertyDetailContent: () => import('../components/PropertyDetailContent'),
  
  // Admin components
  AllHistoryPage: () => import('../pages/AllHistoryPage'),
  PromptsPage: () => import('../pages/PromptsPage'),
  HistoryPage: () => import('../pages/HistoryPage'),
  LogsPage: () => import('../pages/LogsPage'),
  
  // Static pages
  About: () => import('../pages/About'),
  SchedulePage: () => import('../pages/SchedulePage'),
};

/**
 * Preload critical components after initial app load
 * This runs after the user has loaded the initial route
 */
export const preloadCriticalComponents = () => {
  // Use requestIdleCallback for non-blocking preloading
  const preloadWhenIdle = (preloadFn) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => preloadFn().catch(() => {}));
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => preloadFn().catch(() => {}), 100);
    }
  };

  // Preload in order of importance
  preloadWhenIdle(preloadComponents.Dashboard);
  preloadWhenIdle(preloadComponents.ChatInterface);
  preloadWhenIdle(preloadComponents.FindPropertiesPage);
};

/**
 * Preload components based on user navigation patterns
 */
export const preloadOnHover = (componentName) => {
  if (preloadComponents[componentName]) {
    preloadComponents[componentName]().catch(() => {});
  }
};

/**
 * Preload components based on user authentication state
 */
export const preloadForAuthenticatedUser = () => {
  const preloadWhenIdle = (preloadFn) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => preloadFn().catch(() => {}));
    } else {
      setTimeout(() => preloadFn().catch(() => {}), 200);
    }
  };

  // Authenticated users are likely to visit these pages
  preloadWhenIdle(preloadComponents.Dashboard);
  preloadWhenIdle(preloadComponents.Profile);
  preloadWhenIdle(preloadComponents.MyPropertiesPage);
  preloadWhenIdle(preloadComponents.ChatPage);
};

/**
 * Preload property-related components when user shows interest
 */
export const preloadPropertyComponents = () => {
  const preloadWhenIdle = (preloadFn) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => preloadFn().catch(() => {}));
    } else {
      setTimeout(() => preloadFn().catch(() => {}), 300);
    }
  };

  preloadWhenIdle(preloadComponents.FindPropertiesMap);
  preloadWhenIdle(preloadComponents.GoogleMapComponent);
  preloadWhenIdle(preloadComponents.PropertyDetailContent);
};

/**
 * Preload based on route prediction
 */
export const preloadBasedOnRoute = (currentRoute) => {
  const routePreloadMap = {
    '/': ['Dashboard', 'ChatInterface', 'FindPropertiesPage'],
    '/login': ['Dashboard', 'Profile'],
    '/signup': ['Dashboard', 'Profile'],
    '/dashboard': ['Profile', 'MyPropertiesPage', 'FindPropertiesPage'],
    '/properties': ['FindPropertiesMap', 'PropertyDetail', 'GoogleMapComponent'],
    '/properties-map': ['FindPropertiesPage', 'PropertyDetail', 'PropertyDetailContent'],
    '/chat': ['ChatInterface'],
    '/my-properties': ['MyPropertyDashboardPage', 'PropertyDetailContent'],
    '/agent/onboarding': ['AgentDashboard', 'AgentSettingsPage'],
    '/agent-settings': ['AgentOnboardingPage', 'AgentDashboard'],
    '/agent/email': ['AgentDashboard'],
    '/agent/calendar': ['AgentDashboard'],
    '/agent/contacts': ['AgentDashboard'],
    '/agent/properties': ['AgentPropertyWorkspace', 'AgentDashboard'],
    '/agent/approvals': ['AgentPropertyWorkspace', 'AgentDashboard'],
    '/agent/marketing': ['AgentDashboard'],
    '/agent/tasks': ['AgentPropertyWorkspace', 'AgentDashboard'],
    '/agent/inquiries': ['AgentPropertyWorkspace', 'AgentDashboard'],
  };

  const componentsToPreload = routePreloadMap[currentRoute] || [];
  
  componentsToPreload.forEach((componentName, index) => {
    // Stagger preloading to avoid overwhelming the browser
    setTimeout(() => {
      preloadOnHover(componentName);
    }, index * 100);
  });
};

/**
 * Initialize preloading strategies
 */
export const initializePreloading = (currentRoute, isAuthenticated) => {
  // Initial critical preloading
  setTimeout(() => {
    preloadCriticalComponents();
  }, 1000); // Wait 1 second after initial load

  // Route-based preloading
  setTimeout(() => {
    preloadBasedOnRoute(currentRoute);
  }, 2000);

  // Auth-based preloading
  if (isAuthenticated) {
    setTimeout(() => {
      preloadForAuthenticatedUser();
    }, 3000);
  }
};